import { useMutation, useQuery } from '@tanstack/react-query'
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'

import {
  deleteData,
  downloadData,
  getConfigData,
  getData,
  postData,
  updateData,
  updateFromData,
} from '../../apis/api.helpers'
import apiUrl from '../../apis/api.url'
import { QueryParams, SearchParams } from '../../common/types'
import { useSnackbarManager } from '../../components/common/snackbar'
import { getErrorMessage, parseQueryParams } from '../../utilities/parsers'

const buildUrlWithParams = (baseUrl: string, params: any) => {
  return `${baseUrl}${parseQueryParams(params)}`
}

const fetchData = async (input: QueryParams, type?: any) => {
  const url = buildUrlWithParams(apiUrl.ORGANISATION_LIST_URL, {
    ...input,
    ...(type === 'pending' && { is_confirmed: 'True', is_verified: 'False' }),
  })
  const response = await getData(url)
  return response
}

export const useOrganisation = (input: QueryParams, type?: any) => {
  return useQuery(
    ['organisation_list', input, type],
    () => fetchData(input, type),
    {
      // refetchOnWindowFocus: true,
      //staleTime: 50000,
    }
  )
}

export const createOrganisation = (input: any) => {
  return postData(apiUrl.ORGANISATION_LIST_URL, input)
}

export const useCreateOrganisation = (
  handleSubmission: (data: any) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(createOrganisation, {
    onSuccess: (res: AxiosResponse) => {
      handleSubmission(res.data)
      enqueueSnackbar('Organisation Created Successfully', {
        variant: 'success',
      })
    },

    onError: (error: AxiosError | any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.detail), {
        variant: 'error',
      })
    },
  })
}

export const editOrganisation = (data: { input: any; id: string }) => {
  return updateData(`${apiUrl.ORGANISATION_LIST_URL}/${data?.id}`, data?.input)
}

export const useEditOrganisation = (
  handleSubmission: (input: any, id: string) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation(editOrganisation, {
    onSuccess: (res: AxiosResponse | any) => {
      handleSubmission(res.data, res.id)
      enqueueSnackbar('Organisation Info Updated successfully', {
        variant: 'success',
      })
    },

    onError: (error: AxiosError | any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.detail), {
        variant: 'error',
      })
    },
  })
}

export const editOwnOrganisation = (data: { input: any }) => {
  return updateData(`profile`, data?.input)
}

export const useEditOwnOrganisation = (
  handleSubmission: (input: any) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation(editOwnOrganisation, {
    onSuccess: (res: AxiosResponse | any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Organisation Info Updated successfully', {
        variant: 'success',
      })
    },

    onError: (error: AxiosError | any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.detail), {
        variant: 'error',
      })
    },
  })
}

export const getOrganisationDetails = (id?: string) => {
  return getData(`${apiUrl.ORGANISATION_LIST_URL}/${id}`)
}
export const getOrganisationProfile = () => {
  return getData(`profile`)
}
export const updateOrganisationCre = (id: string, data: any) => {
  return postData(`${apiUrl.CONTACT_LIST_URL}${id}/allocate-cre`, data)
}
export const deleteOrganisationItem = (id?: string) => {
  return updateData(`${apiUrl.ORGANISATION_LIST_URL}/${id}/status`, {})
}
export const getOrganisationDuplicate = (
  data: any,
  config: AxiosRequestConfig
) =>
  getConfigData(
    `${apiUrl.CONTACT_LIST_URL}possible-contact-duplicates/${parseQueryParams(
      data ?? {}
    )}`,
    config
  )

export const editOrganisationAddress = (data: { input: any; id: string }) => {
  return updateData(
    `${apiUrl.ORGANISATION_LIST_URL}/${data?.id}/address`,
    data?.input
  )
}

export const useEditOrganisationAddress = (
  handleSubmission: (input: any, id: string) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation(editOrganisationAddress, {
    onSuccess: (res: AxiosResponse | any) => {
      handleSubmission(res.data, res.id)
      enqueueSnackbar('Organisation Address Updated successfully', {
        variant: 'success',
      })
    },

    onError: (error: AxiosError | any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.detail), {
        variant: 'error',
      })
    },
  })
}

export const editOwnOrganisationAddress = (data: { input: any }) => {
  return updateData(`address`, data?.input)
}

export const useEditOwnOrganisationAddress = (
  handleSubmission: (input: any) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation(editOwnOrganisationAddress, {
    onSuccess: (res: AxiosResponse | any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Organisation Address Updated successfully', {
        variant: 'success',
      })
    },

    onError: (error: AxiosError | any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.detail), {
        variant: 'error',
      })
    },
  })
}
export const getOrganisationFileRepositoryInAdminPortal = (
  pageParams: any,
  id: string
) => {
  const data = getData(
    `${apiUrl.ORGANISATION_LIST_URL}/${id}/files${parseQueryParams(pageParams)}`
  )
  return data
}
export const getOrganisationFileRepository = (pageParams: any) => {
  const data = getData(`files${parseQueryParams(pageParams)}`)
  return data
}

export const getOwnOrganisationFileRepository = (pageParams: any) => {
  const data = getData(`files${parseQueryParams(pageParams)}`)
  return data
}

export const getOrganisationUsers = (pageParams: any, id: string) => {
  return getData(
    `${apiUrl.ORGANISATION_LIST_URL}/${id}/contact${parseQueryParams(pageParams)}`
  )
}
export const getOwnOrganisationUsers = (pageParams: any) => {
  // return getData(`contact${parseQueryParams(pageParams)}`)
  return getData(
    `${apiUrl.ORGANISATION_URL}/contact${parseQueryParams(pageParams)}`
  )
}
export const getOwnOrganisationUsersInAdminPortal = (
  pageParams: any,
  id: string
) => {
  return getData(
    `${apiUrl.ORGANISATION_LIST_URL}/${id}/contact${parseQueryParams(pageParams)}`
  )
}

export const getOrgUserDetails = (contact_id: string) => {
  return getData(`${apiUrl.ORGANISATION_URL}/contact/${contact_id}`)
}
export const getOrgUserDetailsInAdminPortal = (
  org_id: string,
  contact_id: string
) => {
  return getData(
    `${apiUrl.ORGANISATION_LIST_URL}/${org_id}/contact/${contact_id}`
  )
}

export const createOrgUser = ({ orgId, data }: any) => {
  if (orgId !== undefined)
    return postData(`${apiUrl.ORGANISATION_LIST_URL}/${orgId}/contact`, data)
  else return postData(`${apiUrl.ORGANISATION_URL}/contact`, data)
}
export const useCreateOrgUser = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(createOrgUser, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)
      enqueueSnackbar('User created successfully', { variant: 'success' })
    },

    onError: (error: any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.message), {
        variant: 'error',
      })
    },
  })
}
export const updateOrgUser = ({ id, data, orgId }: any) => {
  if (orgId !== undefined) {
    return updateFromData(
      `${apiUrl.ORGANISATION_LIST_URL}/${orgId}/contact/${id}`,
      data
    )
  } else {
    return updateFromData(`${apiUrl.ORGANISATION_URL}/contact/${id}`, data)
  }
}
export const useUpdateOrgUser = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(updateOrgUser, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Updated successfully', { variant: 'success' })
    },

    onError: (error: any) => {
      enqueueSnackbar(
        getErrorMessage(
          error?.response?.data?.error ?? error?.response?.data?.detail
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}

export const deactivateOrgUser = (org_id: string, contact_id: string) => {
  return updateFromData(
    `${apiUrl.ORGANISATION_LIST_URL}/${org_id}/contact/${contact_id}/status`,
    {}
  )
}

export const sendOrgUserInvitation = (org_id: string, contact_id: string) => {
  return postData(
    `${apiUrl.ORGANISATION_LIST_URL}/${org_id}/contact/${contact_id}/invite`,
    {}
  )
}

export const createOrganisationContact = (data: { input: any; id: string }) => {
  return postData(
    `${apiUrl.ORGANISATION_LIST_URL}/${data?.id}/business_contact`,
    data?.input
  )
}

export const useCreateOrganisationContact = (
  handleSubmission: (input: any, id: string) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(createOrganisationContact, {
    onSuccess: (res: AxiosResponse | any) => {
      handleSubmission(res.data, res.id)
      enqueueSnackbar('Organisation Contact Updated Successfully', {
        variant: 'success',
      })
    },

    onError: (error: AxiosError | any) => {
      enqueueSnackbar(
        getErrorMessage(
          error?.response?.data?.detail || error?.response?.data?.message
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}

export const editOrganisationContact = (data: {
  input: any
  id: string
  contId: string
}) => {
  return updateData(
    `${apiUrl.ORGANISATION_LIST_URL}/${data?.id}/business_contact/${data?.contId}`,
    data?.input
  )
}

export const useEditOrganisationContact = (
  handleSubmission: (input: any, id: string) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation(editOrganisationContact, {
    onSuccess: (res: AxiosResponse | any) => {
      handleSubmission(res.data, res.id)
      enqueueSnackbar('Organisation Contact Updated successfully', {
        variant: 'success',
      })
    },

    onError: (error: AxiosError | any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.message), {
        variant: 'error',
      })
    },
  })
}

export const editOwnOrganisationContact = (data: { input: any }) => {
  return postData(`business_contact`, data?.input)
}

export const useEditOwnOrganisationContact = (
  handleSubmission: (input: any) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation(editOwnOrganisationContact, {
    onSuccess: (res: AxiosResponse | any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Organisation Contact Updated successfully', {
        variant: 'success',
      })
    },

    onError: (error: AxiosError | any) => {
      enqueueSnackbar(
        getErrorMessage(
          error?.response?.data?.detail || error?.response?.data?.message
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}
export const updateOrgUserPassword = (
  orgId: string,
  employee: string,
  data: string
) => {
  return updateFromData(
    `${apiUrl.ORGANISATION_LIST_URL}/${orgId}/contact/${employee}/change_password`,
    data
  )
}
export const getOrganisationDEIStrategyDetails = () => {
  const data = getData(`dei-strategy`)
  return data
}
export const getOrganisationDEIStrategyDetailsInAdminPortal = (id: string) => {
  const data = getData(`${apiUrl.ORGANISATION_LIST_URL}/${id}/dei-strategy`)
  return data
}

export const getOwnOrganisationDEIStrategyDetails = () => {
  const data = getData(`dei-strategy`)
  return data
}

export const updateDEIStrategy = ({ id, input }: any) => {
  if (id) {
    return updateFromData(
      `${apiUrl.ORGANISATION_LIST_URL}/${id}/dei-strategy`,
      input
    )
  } else {
    return updateFromData(`dei-strategy`, input)
  }
}

export const useUpdateDEIStrategy = (
  handleSubmission: (input: any) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation(updateDEIStrategy, {
    onSuccess: (res: AxiosResponse | any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Updated successfully', {
        variant: 'success',
      })
    },

    onError: (error: AxiosError | any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.detail), {
        variant: 'error',
      })
    },
  })
}

// export const updateDEIStrategyInAdminPortal = ({ id, input }: any) => {
//   return updateFromData(
//     `${apiUrl.ORGANISATION_LIST_URL}/${id}/dei-strategy`,
//     input
//   )
// }
// export const useUpdateDEIStrategyInAdminPortal = (
//   handleSubmission: (input: any) => void
// ) => {
//   const { enqueueSnackbar } = useSnackbarManager()

//   return useMutation(updateDEIStrategyInAdminPortal, {
//     onSuccess: (res: AxiosResponse | any) => {
//       handleSubmission(res.data)
//       enqueueSnackbar('Updated successfully', {
//         variant: 'success',
//       })
//     },

//     onError: (error: AxiosError | any) => {
//       enqueueSnackbar(getErrorMessage(error?.response?.data?.detail), {
//         variant: 'error',
//       })
//     },
//   })
// }
export const createFileUpload = (input: any) => {
  return postData(`files/upload`, input)
}
export const deleteFile = (item: any) => {
  return deleteData(`files/${item}`)
}
export const createFileUploadInAdminPortal = (orgId: string, input: any) => {
  return postData(
    `${apiUrl.ORGANISATION_LIST_URL}/${orgId}/files/upload`,
    input
  )
}
export const deleteFileInAdminPortal = (orgId: string, item: any) => {
  return deleteData(`${apiUrl.ORGANISATION_LIST_URL}/${orgId}/files/${item}`)
}

export const getDataTables = (orgId?: string, type?: string, params?: any) => {
  return getData(
    `${apiUrl.ORGANISATION_LIST_URL}/${orgId}/${apiUrl.ORGANISATION_DATATABLES}/${type}${parseQueryParams(params)}`
  )
}
export const exportDataTable = (orgId: string, type: string, params?: any) => {
  return downloadData(
    `${apiUrl.ORGANISATION_LIST_URL}/${orgId}/${apiUrl.ORGANISATION_DATATABLES}/${type}/export${parseQueryParams(params)}`
  )
}
export const exportOrgDataTable = (type: string, input?: any) => {
  return downloadData(
    `${apiUrl.ORGANISATION_DATATABLES}/${type}/export${parseQueryParams(input)}`
  )
}
export const getOrgDataTables = (type?: string, params?: any) => {
  return getData(
    `${apiUrl.ORGANISATION_DATATABLES}/${type}${parseQueryParams(params)}`
  )
}

export const getOrganisationChangelog = (params: SearchParams, id?: string) => {
  return getData(
    `${apiUrl.ORGANISATION_LIST_URL}/${id}/logs${parseQueryParams(params)}`
  )
}
export const getOwnOrganisationChangelog = (params: SearchParams) => {
  return getData(`logs${parseQueryParams(params)}`)
}

export const getOrganisationCharter = (id?: string) => {
  return getData(`${apiUrl.ORGANISATION_LIST_URL}/${id}/charter`)
}
export const getOwnOrganisationCharter = () => {
  return getData(`charter`)
}
export const VerifyCharter = (data: { input: any; id?: string }) => {
  return updateData(
    `${apiUrl.ORGANISATION_LIST_URL}/${data?.id}/verify-charter`,
    data?.input
  )
}

export const editOwnOrganisationCharter = (data: {
  input: any
  charterId: string
}) => {
  return updateData(`charter/${data?.charterId}`, data?.input)
}

export const useEditOwnOrganisationCharter = (
  handleSubmission: (input: any) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()

  return useMutation(editOwnOrganisationCharter, {
    onSuccess: (res: AxiosResponse | any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Organisation Charter Updated successfully', {
        variant: 'success',
      })
    },

    onError: (error: AxiosError | any) => {
      enqueueSnackbar(
        getErrorMessage(
          error?.response?.data?.detail || error?.response?.data?.message
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}

export const createOwnOrgCharter = ({ data }: any) => {
  return postData(`charter`, data)
}
export const useCreateOwnOrgCharter = (
  handleSubmission: (data: any) => void
) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(createOwnOrgCharter, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Organisation Charter Created Successfully', {
        variant: 'success',
      })
    },

    onError: (error: any) => {
      enqueueSnackbar(
        getErrorMessage(
          error.response.data.error || error?.response?.data?.message
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}

export const deleteFileCharter = (input: { charterId: string }) => {
  return deleteData(`charter/${input?.charterId}`)
}

export const updateOrgDataTables = (data: any, type?: string) => {
  return postData(`${apiUrl.ORGANISATION_DATATABLES}/${type}`, data)
}
export const updateWorkForceLocation = (data: any) => {
  return postData(`datatable/assign-workforce-locations`, data)
}

export const updateOrgLogoInAdminPortal = (orgId: any, input: any) => {
  return updateFromData(`${apiUrl.ORGANISATION_LIST_URL}/${orgId}/logo`, input)
}
export const updateOrgLogo = (input: any) => {
  return updateFromData(`logo`, input)
}

export const getAccreditationEligibility = (id?: string) => {
  return getData(`${apiUrl.ORGANISATION_LIST_URL}/${id}/eligibility`)
}

export const changeEligibility = (data: any, id?: string) => {
  return postData(
    `${apiUrl.ORGANISATION_LIST_URL}/${id}/change-application-eligibility`,
    data
  )
}

export const changeRenEligibility = (data: any, id?: string) => {
  return postData(
    `${apiUrl.ORGANISATION_LIST_URL}/${id}/change-renewal-eligibility`,
    data
  )
}

export const deleteSignature = (id: string) => {
  return deleteData(`business_contact/${id}/signature`)
}

export const deleteSignatureInAdminPortal = (orgId: string, id: string) => {
  return deleteData(`organisations/${orgId}/business_contact/${id}/signature`)
}

export const getPrimaryLocationHistory = () => {
  return getData(`datatable/assign-workforce-locations`)
}
export const getAdminPortalPrimaryLocationHistory = (orgId: any) => {
  return getData(
    `${apiUrl.ORGANISATION_LIST_URL}/${orgId}/datatable/assign-workforce-locations`
  )
}
export const getDataTableVersionList = (orgId: any) => {
  return getData(
    `${apiUrl.ORGANISATION_LIST_URL}/${orgId}/${apiUrl.ORG_DATATABLE_VERSION_LIST}`
  )
}

export const getOrganisationMainNote = (orgId: string) => {
  return getData(`${apiUrl.ORGANISATION_LIST_URL}/${orgId}/main-note`)
}

export const updateOrganisationMainNote = (
  orgId: string,
  payload: { content: string; reminder_date?: string | null }
) => {
  return updateData(
    `${apiUrl.ORGANISATION_LIST_URL}/${orgId}/main-note`,
    payload
  )
}
